<div class="container">

  <header class="page-heading">
    <h1>SIP vs Step-Up SIP Calculator</h1>
    <p>Compare a flat monthly SIP against a step-up SIP with the same starting amount, return and tenure — side by side.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Comparison Inputs</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyInvestment')">
        <label class="field-label" for="monthlyInvestment">Monthly Investment (Year 1)</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyInvestment" type="number"
            [(ngModel)]="monthlyInvestment"
            [min]="C.monthlyInvestment.min" [max]="C.monthlyInvestment.max" [step]="C.monthlyInvestment.step"
            (ngModelChange)="onFieldChange('monthlyInvestment')"
            (blur)="onFieldBlur('monthlyInvestment')"
            placeholder="e.g. 10,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">Same starting amount is used for both the flat SIP and the step-up SIP.</div>
        @if (hasError('monthlyInvestment')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyInvestment') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('stepUpPercent')">
        <label class="field-label" for="stepUpPercent">Annual Step-Up</label>
        <div class="input-shell suffix">
          <input id="stepUpPercent" type="number"
            [(ngModel)]="stepUpPercent"
            [min]="C.stepUpPercent.min" [max]="C.stepUpPercent.max" [step]="C.stepUpPercent.step"
            (ngModelChange)="onFieldChange('stepUpPercent')"
            (blur)="onFieldBlur('stepUpPercent')"
            placeholder="e.g. 10"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Applies only to the step-up SIP side of the comparison.</div>
        @if (hasError('stepUpPercent')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('stepUpPercent') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Expected Annual Return</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 12"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Used for both sides of the comparison.</div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('tenureYears')">
        <label class="field-label" for="tenureYears">Investment Duration</label>
        <div class="input-shell suffix">
          <input id="tenureYears" type="number"
            [(ngModel)]="tenureYears"
            [min]="C.tenureYears.min" [max]="C.tenureYears.max" [step]="C.tenureYears.step"
            (ngModelChange)="onFieldChange('tenureYears')"
            (blur)="onFieldBlur('tenureYears')"
            placeholder="e.g. 10"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('tenureYears')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('tenureYears') }}
          </div>
        }
      </div>
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Compare
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to compare
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-purple">
        <div class="sc-icon"></div>
        <div class="sc-label">Flat SIP Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.flatMaturityValue) }}</div>
        <div class="sc-sub">{{ fmt.currency(monthlyInvestment) }}/mo, unchanged</div>
      </div>
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Step-Up SIP Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">starts {{ fmt.currency(monthlyInvestment) }}/mo, +{{ stepUpPercent }}%/yr</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Extra Corpus from Step-Up</div>
        <div class="sc-value">{{ fmt.currency(result.extraCorpus) }}</div>
        <div class="sc-sub">{{ extraPercent }}% more than the flat SIP</div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Side-by-Side Comparison</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Metric</th>
              <th class="t-balance">Flat SIP</th>
              <th class="t-balance">Step-Up SIP</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="t-left">Starting Monthly Installment</td>
              <td class="t-balance">{{ fmt.currency(monthlyInvestment) }}</td>
              <td class="t-balance">{{ fmt.currency(monthlyInvestment) }}</td>
            </tr>
            <tr>
              <td class="t-left">Final Monthly Installment</td>
              <td class="t-balance">{{ fmt.currency(monthlyInvestment) }}</td>
              <td class="t-balance">{{ fmt.currency(result.years[result.years.length - 1].installment) }}</td>
            </tr>
            <tr>
              <td class="t-left">Total Invested</td>
              <td class="t-balance">{{ fmt.currency(monthlyInvestment * tenureYears * 12) }}</td>
              <td class="t-balance">{{ fmt.currency(result.totalInvested) }}</td>
            </tr>
            <tr>
              <td class="t-left"><strong>Maturity Value</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.flatMaturityValue) }}</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.maturityValue) }}</strong></td>
            </tr>
            <tr>
              <td class="t-left">Wealth Gained</td>
              <td class="t-balance">{{ fmt.currency(result.flatMaturityValue - (monthlyInvestment * tenureYears * 12)) }}</td>
              <td class="t-balance">{{ fmt.currency(result.totalGain) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Growth: Flat SIP vs Step-Up SIP</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap">
          <canvas #growthRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-Wise Comparison</div>

      <div class="tab-bar">
        <button type="button" class="tab-btn" [class.active]="activeTab==='yearly'" (click)="activeTab='yearly'">
          Yearly Summary
        </button>
        <button type="button" class="tab-btn" [class.active]="activeTab==='monthly'" (click)="activeTab='monthly'">
          Full Monthly Schedule
        </button>
        <div class="tab-count">
          @if (activeTab === 'yearly') { {{ result.years.length }} years }
          @else { {{ result.months.length }} months }
        </div>
      </div>

      @if (activeTab === 'yearly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">Year</th>
                <th>Step-Up Installment</th>
                <th class="t-balance">Flat SIP Corpus</th>
                <th class="t-balance">Step-Up SIP Corpus</th>
              </tr>
            </thead>
            <tbody>
              @for (row of result.years; track row.year) {
                <tr>
                  <td class="t-left t-year">Year {{ row.year }}</td>
                  <td>{{ fmt.currency(row.installment) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.flatBalance) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (activeTab === 'monthly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">#</th>
                <th>Step-Up Installment</th>
                <th class="t-balance">Step-Up SIP Corpus</th>
              </tr>
            </thead>
            <tbody>
              @for (row of result.months; track row.month) {
                <tr>
                  <td class="t-left t-num">Month {{ row.month }}</td>
                  <td>{{ fmt.currency(row.installment) }}</td>
                  <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">SIP vs Step-Up SIP: what actually changes</h2>
    <p>
      A flat SIP invests the same amount every month for the whole tenure. A step-up SIP starts at
      the same amount but increases it by a fixed percentage every year. This calculator runs both
      scenarios on identical starting amount, expected return and tenure, so the only variable is
      whether your installment grows or stays flat.
    </p>
    <p>
      The step-up SIP always ends with a larger corpus for the same starting amount, because it puts
      more money to work — but it also means committing to invest more each year, not just once.
      The "extra corpus" figure above is the trade-off you're weighing against that rising
      commitment.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is the step-up SIP always the better choice?</h3>
      <p>
        It always builds a larger corpus for the same starting amount and return, but "better"
        depends on whether your income can actually sustain the rising installment every year. A
        flat SIP is the safer choice if your cash flow isn't reliably growing.
      </p>
    </div>
    <div class="faq-item">
      <h3>Why is the step-up SIP's total invested amount higher?</h3>
      <p>
        Because the installment itself grows every year, so the sum of all installments over the
        tenure is naturally higher than a flat SIP of the same starting amount. The comparison table
        above shows exactly how much more was invested, and how much of the final gap is genuinely
        extra wealth versus extra capital put in.
      </p>
    </div>
    <div class="faq-item">
      <h3>What step-up percentage should I use in this comparison?</h3>
      <p>
        A common approach is to match your expected annual salary hike (commonly 8&ndash;10%), so
        the comparison reflects a step-up you can realistically sustain rather than an
        aspirational number.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more:
      <a routerLink="/learn/sip-vs-step-up-sip">SIP vs step-up SIP: which builds more wealth?</a>,
      <a routerLink="/learn/step-up-sip-explained">what is a step-up SIP, and how much extra wealth does it build?</a> or
      <a routerLink="/learn/sip-vs-lumpsum">SIP vs lump sum: what does the math actually say?</a>
    </p>
    <p class="learn-crosslink">
      Also see: <a routerLink="/sip">Flat SIP Calculator</a> and
      <a routerLink="/step-up-sip">Step-Up SIP Calculator</a>
    </p>
  </div>
</section>
